import React, { useMemo, useState, useRef } from 'react';
import '../../styles/ProgressionUI/ExerciseProgressionChart.css';

const ExerciseProgressionChart = ({ exercise, history }) => {
  const [hoveredPointIndex, setHoveredPointIndex] = useState(null);
  const chartRef = useRef(null);
  
  // Sort history by date (oldest first)
  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [history]);

  // Don't render if there's no history
  if (!sortedHistory.length) {
    return (
      <div className="no-data">
        <h2>{exercise}</h2>
        <p>No progression data available for this exercise.</p>
      </div>
    );
  }

  // Find min and max date for scaling x-axis
  const minDate = new Date(sortedHistory[0].date).getTime();
  const maxDate = new Date(sortedHistory[sortedHistory.length - 1].date).getTime();
  const dateRange = maxDate - minDate;

  // Find min and max weight for scaling
  const minWeight = 0; // Always start y-axis at 0
  const maxWeight = Math.max(1, Math.max(...sortedHistory.map(entry => entry.weight))) * 1.25;
  
  // Find the max successful weight entry
  // If multiple entries have the same max weight, use the most recent one
  const maxSuccessfulEntry = sortedHistory
    .filter(entry => entry.wasSuccessful)
    .reduce((max, entry) => {
      // If weights are equal, keep the most recent one
      if (entry.weight === max.weight) {
        return new Date(entry.date) > new Date(max.date) ? entry : max;
      }
      // Otherwise, keep the maximum weight
      return entry.weight > max.weight ? entry : max;
    }, { weight: 0, date: null });
  
  const maxSuccessfulWeight = maxSuccessfulEntry.weight;
  const maxSuccessfulDate = maxSuccessfulEntry.date;

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  };

  // Convert data for chart
  const chartData = sortedHistory.map(entry => ({
    date: formatDate(entry.date),
    rawDate: entry.date, // Store original date for comparison
    timestamp: new Date(entry.date).getTime(), // Store timestamp for proportional scaling
    weight: entry.weight,
    success: entry.wasSuccessful
  }));

  // Calculate x position based on proportional date
  const getXPosition = (timestamp) => {
    // Single point case
    if (dateRange === 0) return (fixedChartWidth - 100) / 2;
    
    // Calculate position based on date proportion
    const dateProportion = (timestamp - minDate) / dateRange;
    return dateProportion * (fixedChartWidth - 100);
  };

  // Handler for mouse movement over the chart
  const handleMouseMove = (event) => {
    if (!chartRef.current) return;
    
    const svgRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - svgRect.left;
    
    // Scale mouse X position to chart coordinate system
    const scaledMouseX = (mouseX / svgRect.width) * 600;
    
    // Find point with closest x-coordinate to mouse position
    let closestIndex = -1;
    let minXDistance = Infinity;
    
    chartData.forEach((point, i) => {
      // Transform point to SVG space (accounting for margins)
      const x = getXPosition(point.timestamp) + 60;
      
      // Calculate horizontal distance only
      const xDistance = Math.abs(scaledMouseX - x);
      
      if (xDistance < minXDistance) {
        minXDistance = xDistance;
        closestIndex = i;
      }
    });
    
    setHoveredPointIndex(closestIndex);
  };
  
  // Handler for mouse leaving the chart
  const handleMouseLeave = () => {
    setHoveredPointIndex(null);
  };
  
  // Fixed chart width
  const fixedChartWidth = 600;
  
  return (
    <div className="exercise-progression-chart">
      <h2>{exercise} Progression</h2>
      
      <div className="chart-container">
        <svg 
          ref={chartRef}
          className="chart"
          viewBox="0 0 600 400" 
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="400"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Define margins */}
          <g transform="translate(60, 20)">
            {/* Draw Y axis */}
            <line x1="0" y1="0" x2="0" y2="300" stroke="#ecf0f1" strokeWidth="1" />
            
            {/* Y axis label - Show for hovered point or max successful weight if no hover */}
            {hoveredPointIndex !== null ? (
              <text 
                x="-10" 
                y={300 - (chartData[hoveredPointIndex].weight / maxWeight * 300)} 
                textAnchor="end" 
                dominantBaseline="middle" 
                className="axis-text"
              >
                {chartData[hoveredPointIndex].weight} lbs
              </text>
            ) : (
              maxSuccessfulWeight > 0 && (
                <text 
                  x="-10" 
                  y={300 - (maxSuccessfulWeight / maxWeight * 300)} 
                  textAnchor="end" 
                  dominantBaseline="middle" 
                  className="axis-text"
                >
                  {maxSuccessfulWeight} lbs
                </text>
              )
            )}
            
            {/* Y axis ticks - Only at hovered point (or max successful weight) and zero */}
            <line x1="-5" y1="300" x2="0" y2="300" stroke="#bdc3c7" strokeWidth="1" />
            {hoveredPointIndex !== null ? (
              <line 
                x1="-5" 
                y1={300 - (chartData[hoveredPointIndex].weight / maxWeight * 300)} 
                x2="0" 
                y2={300 - (chartData[hoveredPointIndex].weight / maxWeight * 300)} 
                stroke="#bdc3c7" 
                strokeWidth="1" 
              />
            ) : (
              maxSuccessfulWeight > 0 && (
                <line 
                  x1="-5" 
                  y1={300 - (maxSuccessfulWeight / maxWeight * 300)} 
                  x2="0" 
                  y2={300 - (maxSuccessfulWeight / maxWeight * 300)} 
                  stroke="#bdc3c7" 
                  strokeWidth="1" 
                />
              )
            )}
            
            {/* Draw X axis */}
            <line 
              x1="0" 
              y1="300" 
              x2={fixedChartWidth - 100} 
              y2="300" 
              stroke="#ecf0f1" 
              strokeWidth="1" 
            />
            
            {/* Plot data points and connecting line */}
            <polyline
              className="line"
              points={chartData.map(point => {
                const x = getXPosition(point.timestamp);
                const y = 300 - (point.weight / maxWeight * 300);
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#3498db"
              strokeWidth="2"
            />
            
            {/* Data points */}
            {chartData.map((point, i) => {
              const x = getXPosition(point.timestamp);
              const y = 300 - (point.weight / maxWeight * 300);
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={y}
                    r="5"
                    className={`data-point ${point.success ? 'success' : 'failure'}`}
                    fill={point.success ? "#3498db" : "white"}
                    stroke="#3498db"
                    strokeWidth="2"
                  />
                  
                  {/* X axis ticks */}
                  <line 
                    x1={x} 
                    y1="300" 
                    x2={x} 
                    y2="305" 
                    stroke="#bdc3c7" 
                    strokeWidth="1" 
                  />
                  
                  {/* Show date and weight for hovered point or max successful point if no hover */}
                  {(hoveredPointIndex === i) || 
                   (hoveredPointIndex === null && point.weight === maxSuccessfulWeight && 
                    point.success && point.rawDate === maxSuccessfulEntry.date) ? (
                    <>
                      <text 
                        x={x} 
                        y="325" 
                        textAnchor="middle" 
                        className="axis-text date"
                      >
                        {point.date}
                      </text>
                      <text 
                        x={x} 
                        y="345" 
                        textAnchor="middle" 
                        className="axis-text weight"
                      >
                        {point.weight} lbs
                      </text>
                    </>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default ExerciseProgressionChart;